import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  HStack,
  Td,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { FaUsers } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { listPools, updatePool } from "../../../api/partnersApi";
import ConfirmDialog from "../../../components/Portal/ConfirmDialog";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import {
  PARTNERS_POOL_STATUS_LABELS,
  PARTNERS_POOL_STATUS_LABEL_KEYS,
  PARTNERS_POOL_STATUS_COLORS,
  getStatusLabel,
} from "../../../constants/statuses";
import {
  Panel,
  DataTable,
  StatusBadge,
  MiniIconButton,
  FilterBar,
  FilterSearch,
  EmptyState,
  TablePagination,
} from "../../../components/Portal/ui";
import PoolFormModal from "./PoolFormModal";
import PoolDetailModal from "./PoolDetailModal";

const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

const PartnersPoolList = ({ onTotalChange }) => {
  const toast = useToast();
  const { t } = useTranslation("portal");
  const { selectedBusinessUnitId, isLoaded: isBUContextLoaded } = useBusinessUnit();

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  // Modals
  const createModal = useDisclosure();
  const editModal = useDisclosure();
  const detailModal = useDisclosure();
  const [editingPool, setEditingPool] = useState(null);
  const [selectedPool, setSelectedPool] = useState(null);
  const archiveConfirmDialog = useDisclosure();
  const [archiveTarget, setArchiveTarget] = useState(null);

  const refreshTimerRef = useRef(null);

  const fetchPools = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await listPools(
        {
          page,
          pageSize,
          search: search || undefined
        },
        { buId: selectedBusinessUnitId || undefined }
      );
      setItems(res.items || []);
      setTotalItems(res.totalItems || 0);
      setTotalPages(
        res.totalPages || Math.max(1, Math.ceil((res.totalItems || 0) / pageSize))
      );
    } catch (e) {
      if (!silent) {
        toast({
          title: t("partners.pools.toasts.loadFailed", "Failed to load pools"),
          status: "error"
        });
      }
      setItems([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, pageSize, search, selectedBusinessUnitId, toast, t]);

  useEffect(() => {
    if (!isBUContextLoaded) return;
    fetchPools();
  }, [fetchPools, isBUContextLoaded]);

  // Surface the total to the parent KPI strip.
  useEffect(() => {
    if (onTotalChange) onTotalChange(totalItems);
  }, [totalItems, onTotalChange]);

  // Auto-refresh every 30 seconds (silent)
  useEffect(() => {
    if (!isBUContextLoaded) return;
    refreshTimerRef.current = setInterval(() => {
      fetchPools(true);
    }, AUTO_REFRESH_INTERVAL);
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [fetchPools, isBUContextLoaded]);

  const handleDelete = async (pool) => {
    try {
      await updatePool(pool.id, { status: "archived" }, { buId: selectedBusinessUnitId || undefined });
      toast({
        title: t("partners.pools.toasts.archived", "Pool archived"),
        status: "success"
      });
      fetchPools();
    } catch (e) {
      toast({
        title: t("partners.pools.toasts.archiveFailedTitle", "Archive failed"),
        description: e?.response?.data?.message || t("partners.pools.toasts.archiveFailedDescription", "Could not archive pool."),
        status: "error"
      });
    }
  };

  const handleRowClick = (pool) => {
    setSelectedPool(pool);
    detailModal.onOpen();
  };

  const handleEditClick = (e, pool) => {
    e.stopPropagation();
    setEditingPool(pool);
    editModal.onOpen();
  };

  const handleDeleteClick = (e, pool) => {
    e.stopPropagation();
    setArchiveTarget(pool);
    archiveConfirmDialog.onOpen();
  };

  const handleSuccess = () => {
    fetchPools();
  };

  const columns = [
    { key: "name", label: t("partners.pools.table.name", "Name"), width: "22%" },
    { key: "code", label: t("partners.pools.table.code", "Code"), width: "16%" },
    { key: "description", label: t("partners.pools.table.description", "Description") },
    { key: "status", label: t("partners.pools.table.status", "Status"), width: "14%" },
    { key: "members", label: t("partners.pools.table.members", "Members"), width: "11%", align: "right" },
    { key: "actions", label: t("partners.pools.table.actions", "Actions"), width: "15%", align: "right" },
  ];

  return (
    <Panel
      h="100%"
      w="100%"
      title={t("partners.pools.title", "Consultant Pools")}
      count={
        loading
          ? t("partners.pools.summary.loading", "Loading...")
          : t("partners.pools.summary.total", { count: totalItems, defaultValue: "{{count}} pool(s)" })
      }
      actions={
        <Button
          variant="primary"
          size="sm"
          h="28px"
          px="10px"
          fontSize="11px"
          leftIcon={<AddIcon boxSize="9px" />}
          onClick={createModal.onOpen}
        >
          {t("partners.pools.createButton", "Create Pool")}
        </Button>
      }
      filters={
        <FilterBar>
          <FilterSearch
            placeholder={t("partners.pools.filters.searchPlaceholder", "Search by name or code...")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </FilterBar>
      }
      footer={
        <TablePagination
          page={page}
          pageSize={pageSize}
          totalItems={totalItems}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={(sz) => { setPageSize(sz); setPage(1); }}
        />
      }
    >
      <DataTable
        columns={columns}
        rows={items}
        loading={loading}
        emptyState={
          <EmptyState
            icon={<FaUsers />}
            title={t("partners.pools.table.noPools", "No pools found")}
            hint={t("partners.pools.table.noPoolsHint", "Create a pool to start grouping your consultants.")}
          />
        }
        renderRow={(pool) => (
          <Tr
            key={pool.id}
            cursor="pointer"
            onClick={() => handleRowClick(pool)}
          >
            <Td fontWeight="700">{pool.name || "-"}</Td>
            <Td>
              <Box
                as="span"
                fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                color="portal.primary"
                fontWeight="800"
              >
                {pool.code || "-"}
              </Box>
            </Td>
            <Td title={pool.description || ""}>{pool.description || "-"}</Td>
            <Td>
              {pool.status ? (
                <StatusBadge
                  label={getStatusLabel(
                    t,
                    PARTNERS_POOL_STATUS_LABEL_KEYS,
                    PARTNERS_POOL_STATUS_LABELS,
                    pool.status
                  )}
                  colorScheme={PARTNERS_POOL_STATUS_COLORS[pool.status] || "gray"}
                />
              ) : (
                "-"
              )}
            </Td>
            <Td textAlign="right">{pool.memberCount ?? pool.members_count ?? pool.membersCount ?? "-"}</Td>
            <Td>
              <HStack justify="flex-end" spacing="6px">
                <MiniIconButton
                  aria-label={t("partners.pools.actions.editAria", "Edit")}
                  icon={<EditIcon />}
                  onClick={(e) => handleEditClick(e, pool)}
                />
                <MiniIconButton
                  aria-label={t("partners.pools.actions.archiveAria", "Archive")}
                  icon={<DeleteIcon />}
                  color="portal.danger"
                  _hover={{ bg: "danger.50", borderColor: "portal.danger", color: "portal.danger" }}
                  onClick={(e) => handleDeleteClick(e, pool)}
                />
              </HStack>
            </Td>
          </Tr>
        )}
      />

      {/* Modals */}
      <PoolFormModal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
        initialData={null}
        onSuccess={handleSuccess}
      />
      <PoolFormModal
        isOpen={editModal.isOpen}
        onClose={() => {
          setEditingPool(null);
          editModal.onClose();
        }}
        initialData={editingPool}
        onSuccess={handleSuccess}
      />
      <PoolDetailModal
        isOpen={detailModal.isOpen}
        onClose={() => {
          setSelectedPool(null);
          detailModal.onClose();
        }}
        pool={selectedPool}
      />
      <ConfirmDialog
        isOpen={archiveConfirmDialog.isOpen}
        onClose={() => { setArchiveTarget(null); archiveConfirmDialog.onClose(); }}
        onConfirm={() => handleDelete(archiveTarget)}
        title={t("partners.pools.archiveConfirmTitle", { defaultValue: "Archive pool?" })}
        body={t("partners.pools.confirmArchive", { name: archiveTarget?.name, defaultValue: `Are you sure you want to archive "${archiveTarget?.name}"?` })}
        confirmLabel={t("partners.pools.archiveConfirmButton", { defaultValue: "Archive" })}
        colorScheme="orange"
      />
    </Panel>
  );
};

export default PartnersPoolList;
